import { yupResolver } from '@hookform/resolvers/yup';
import { MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import constants from 'Constants';
import { getRandomFourDigitString } from 'modules/CreateNewRoomModal/CreateNewRoomDataProvider/utils';
import { getDefaultTimezone } from 'shared/components/ScheduleVideoMeetingModal/TimezoneSelect';
import { isSystemUser } from 'utils/matrixUtils';
import { checkForDateInFuture } from 'utils/utils';

import { ScheduleMeetingFormValues } from './types';
import { SlugAvailabilityStatus } from './useSlugAvailability';
import { combineDateAndTime } from './utils';

// When requireTimes=false (inline-edit non-scheduled path) the four time fields are allowed
// to remain null. The base schema covers the create-meeting case (requireTimes=true).
export const buildScheduleMeetingSchema = (requireTimes = true) => {
  const dateField = requireTimes ? yup.date().nullable().required() : yup.date().nullable();
  return yup.object({
    name: yup
      .string()
      .trim()
      .required('schedule_meeting.name_required_error')
      .min(3, 'schedule_meeting.name_min_error')
      .max(50, 'schedule_meeting.name_max_error'),
    description: yup
      .string()
      .trim()
      .max(constants.MAX_TOPIC_LENGTH, 'schedule_meeting.description_max_error')
      .default(''),
    slug: yup
      .string()
      .required('schedule_meeting.slug_required_error')
      .min(3, 'schedule_meeting.slug_min_error')
      .max(50, 'schedule_meeting.slug_max_error')
      .matches(/^[a-z0-9-]+$/i, 'schedule_meeting.slug_pattern_error'),
    slugPostfix: yup.string().required(),
    hasSlugChanged: yup.boolean().required(),
    groupChat: yup.boolean().required(),
    startDate: dateField,
    startTime: dateField,
    endDate: dateField,
    endTime: dateField,
    timezone: yup.number().required(),
    recurrence: yup
      .mixed<ScheduleMeetingFormValues['recurrence']>()
      .oneOf(['none', 'daily', 'weekly', 'monthly'])
      .required(),
    participants: yup.array().of(yup.string().required()).default([]),
    isOrganisationMeeting: yup.boolean().required(),
    // Optional inline-edit fields — passthrough validation; conditional UI controls visibility.
    isPrivateRoom: yup.boolean().optional(),
    isWaitingListEnabled: yup.boolean().optional(),
  });
};

interface UseScheduleMeetingFormProps {
  messengerRoomId?: string;
  matrixClient?: MatrixClient;
  /** Optional prefill values. Spread over base defaults; missing keys keep the base default. */
  defaultValues?: Partial<ScheduleMeetingFormValues>;
  /**
   * When false, Start/End date+time fields are allowed to be null (used by VideoHubInlineEditForm
   * on non-scheduled meetings where the time fields are not rendered). Defaults to true to
   * preserve the existing create-meeting behaviour.
   */
  requireTimes?: boolean;
}

export const computeIsValid = (
  values: Pick<
    ScheduleMeetingFormValues,
    'name' | 'slug' | 'startDate' | 'startTime' | 'endDate' | 'endTime' | 'timezone'
  >,
  slugAvailability: SlugAvailabilityStatus,
  requireTimes: boolean = true
): { isValid: boolean; isStartInPast: boolean; isEndBeforeStart: boolean } => {
  const startDateTime = combineDateAndTime(values.startDate, values.startTime);
  const endDateTime = combineDateAndTime(values.endDate, values.endTime);

  const isStartInPast =
    startDateTime !== null &&
    !checkForDateInFuture({ startDate: values.startDate, startTime: values.startTime, timeZone: values.timezone });
  const isEndBeforeStart =
    startDateTime !== null && endDateTime !== null && endDateTime.getTime() <= startDateTime.getTime();

  const baseValid =
    values.name.trim().length > 0 &&
    values.slug.length > 0 &&
    slugAvailability === 'AVAILABLE' &&
    !isStartInPast &&
    !isEndBeforeStart;

  const timesValid = requireTimes
    ? values.startDate !== null && values.startTime !== null && values.endDate !== null && values.endTime !== null
    : true;

  return { isValid: baseValid && timesValid, isStartInPast, isEndBeforeStart };
};

const useScheduleMeetingForm = ({
  messengerRoomId,
  matrixClient,
  defaultValues,
  requireTimes = true,
}: UseScheduleMeetingFormProps = {}) => {
  const postfix = useMemo(() => getRandomFourDigitString(), []);

  // Snapshot room members once at open time — synchronous, no async needed
  const initialParticipants = useMemo(() => {
    if (!messengerRoomId || !matrixClient) return [];
    const currentUserId = matrixClient.getUserId();
    return (
      matrixClient
        .getRoom(messengerRoomId)
        ?.getJoinedMembers()
        .filter((m) => !isSystemUser(m.userId) && m.userId !== currentUserId)
        .map((m) => m.userId) ?? []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty — snapshot at modal open time only

  const resolver = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => yupResolver(buildScheduleMeetingSchema(requireTimes)) as any,
    [requireTimes]
  );

  const form = useForm<ScheduleMeetingFormValues>({
    mode: 'all',
    resolver,
    defaultValues: {
      name: '',
      description: '',
      slug: '',
      slugPostfix: postfix,
      hasSlugChanged: false,
      groupChat: !!messengerRoomId,
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      timezone: getDefaultTimezone(),
      recurrence: 'none',
      participants: initialParticipants,
      isOrganisationMeeting: false,
      ...defaultValues,
    },
  });

  return { form, postfix };
};

export default useScheduleMeetingForm;
